import React, { type ForwardedRef, forwardRef } from "react";
import type { ActionProps } from "@purpur/action";
import type { BaseProps } from "@purpur/common-types";
import { Icon } from "@purpur/icon";
import { arrowLeft } from "@purpur/icon/assets/arrow-left";
import { arrowRight } from "@purpur/icon/assets/arrow-right";
import { external } from "@purpur/icon/assets/external";
import { phone } from "@purpur/icon/assets/phone";
import c from "classnames/bind";

import styles from "./cta-link.module.scss";

const cx = c.bind(styles);
const rootClassName = "purpur-cta-link";

export const ctaLinkIcons = ["forward", "external", "back", "phone"] as const;
export type CtaLinkIcon = (typeof ctaLinkIcons)[number];

const iconMapping = {
  forward: arrowRight,
  external: external,
  back: arrowLeft,
  phone: phone,
};

type CtaLinkBaseProps = {
  icon?: CtaLinkIcon;
  showIcon?: boolean;
};

export type CtaAnchorProps = ActionProps &
  Omit<BaseProps<"a">, "href"> & {
    href: string;
  };

type SpanProps = ActionProps & BaseProps<"span">;

export type CtaLinkProps = CtaLinkBaseProps & (CtaAnchorProps | SpanProps);

export const CtaLink = forwardRef<HTMLAnchorElement | HTMLSpanElement, CtaLinkProps>(
  (
    {
      children,
      className,
      ["data-testid"]: dataTestid,
      fullWidth = false,
      iconOnly = false,
      showIcon = true,
      negative = false,
      size = "md",
      variant,
      icon = "forward",
      ...props
    },
    ref
  ) => {
    const classes = cx(
      [
        className,
        rootClassName,
        `${rootClassName}--${negative ? `${variant}-negative` : variant}`,
        `${rootClassName}--${size}`,
      ],
      {
        [`${rootClassName}--icon-only`]: iconOnly,
        [`${rootClassName}--negative`]: negative,
        [`${rootClassName}--full-width`]: fullWidth,
      }
    );

    const MappedIcon = () => (
      <Icon
        svg={iconMapping[icon]}
        size={size === "lg" ? "sm" : "xs"}
        className={cx(`${rootClassName}__icon`)}
      />
    );

    const childrenWithIcons = (
      <>
        {showIcon && (icon === "back" || icon === "phone") && <MappedIcon />}
        {(!iconOnly || (iconOnly && !showIcon)) && children}
        {showIcon && (icon === "forward" || icon === "external") && <MappedIcon />}
      </>
    );

    const sharedProps = {
      ...props,
      className: classes,
      ["data-testid"]: dataTestid,
    };

    return "href" in props && props.href ? (
      <a {...sharedProps} ref={ref as ForwardedRef<HTMLAnchorElement>}>
        {childrenWithIcons}
      </a>
    ) : (
      <span {...sharedProps} ref={ref as ForwardedRef<HTMLSpanElement>}>
        {childrenWithIcons}
      </span>
    );
  }
);

CtaLink.displayName = "CtaLink";
